/**
 * Base API configuration and utilities
 */

const STRAPI_URL =
  process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337';

/**
 * Fetch data from Strapi API with cache control
 */
export async function fetchFromStrapi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    // Default fetch options with cache control
    const defaultOptions: RequestInit = {
      cache: 'no-store', // Disable caching for dynamic content
      next: {
        revalidate: 0, // Always revalidate
        tags: ['strapi-content'], // Tag for cache invalidation
      },
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
        ...options.headers,
      },
    };

    const response = await fetch(`${STRAPI_URL}/api${endpoint}`, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error fetching from ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Fetch data from Strapi API with specific cache settings
 */
export async function fetchFromStrapiWithCache<T>(
  endpoint: string,
  cacheOptions: { revalidate?: number; tags?: string[] } = {}
): Promise<T> {
  try {
    const { revalidate = 60, tags = ['strapi-content'] } = cacheOptions;

    const response = await fetch(`${STRAPI_URL}/api${endpoint}`, {
      next: {
        revalidate,
        tags,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error fetching from ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Get the full URL for a Strapi media file
 * @param url - The relative URL from Strapi
 * @returns Full URL for the media file
 */
export function getStrapiMediaUrl(url: string): string {
  if (!url) return '';

  // If it's already a full URL, return as is
  if (url.startsWith('http')) {
    return url;
  }

  // Otherwise, prepend the API base URL
  return `${STRAPI_URL}${url}`;
}

export { STRAPI_URL };
